// src/Studio/engine/motionCompositionEngine.ts
// Composable motion effects engine - multiple effects stack instead of overwrite

import type { Element } from "../types";
import type { MotionConfig, MotionCurve } from "../presets/motionPresetSchema";
import type { EasingType } from "./keyframeEngine";
import { getEasingFunction } from "./keyframeEngine";

// ============ TYPES ============

/**
 * A single motion effect that can be composed with others
 */
export interface MotionEffect {
  /** Unique ID for this effect instance */
  id: string;
  /** Preset ID this effect came from */
  presetId: string;
  /** Human-readable name */
  name: string;
  /** The motion configuration */
  config: MotionConfig;
  /** Whether this effect is enabled */
  enabled?: boolean;
  /** Timing base for this effect (local = clip-relative, global = timeline seconds) */
  timeBase?: "local" | "global";
  /** Priority for ordering (lower = applied first) */
  priority?: number;
}

/**
 * Intermediate values from evaluating a single effect
 * Uses OFFSETS/FACTORS that compose, not absolute values
 */
interface EffectContribution {
  // Position offsets (additive)
  dx: number;
  dy: number;
  // Scale factors (multiplicative, 1.0 = no change)
  scaleFactorX: number;
  scaleFactorY: number;
  // Rotation offset (additive)
  rotationOffset: number;
  // Opacity factor (multiplicative, 1.0 = no change)
  opacityFactor: number;
}

/**
 * Final composed transform result
 */
export interface ComposedTransform {
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  rotation: number;
  opacity: number;
}

// ============ EASING CONVERSION ============

function curveToEasingFn(curve: MotionCurve): (t: number) => number {
  const mapping: Record<MotionCurve, EasingType> = {
    linear: "linear",
    ease_in: "easeIn",
    ease_out: "easeOut",
    ease_in_out: "easeInOut",
    ease_in_cubic: "easeInCubic",
    ease_out_cubic: "easeOutCubic",
    ease_in_back: "easeInBack",
    ease_out_back: "easeOutBack",
    bounce: "easeOut", // Approximation
  };
  return getEasingFunction(mapping[curve] ?? "linear");
}

// ============ INTERPOLATION ============

/**
 * Interpolate a value between start and end based on time within window.
 * Supports animation modes:
 * - "once" (default): linear start→end
 * - "pingPong": start→end→start (there and back)
 * - "oscillate": dampened sinusoidal oscillation between start and end
 *
 * speedMultiplier affects the internal animation rate within the window:
 * - "oscillate": multiplies oscillation frequency (more shakes per second)
 * - "pingPong": multiplies cycle rate (more back-and-forth cycles)
 * - "once": no effect (timing window boundaries control speed directly)
 */
function interpolateValue(
  time: number,
  startTime: number,
  endTime: number,
  startValue: number,
  endValue: number,
  curve: MotionCurve,
  mode?: "once" | "pingPong" | "oscillate",
  oscillations?: number,
  speedMultiplier?: number
): number {
  // Before animation window
  if (time <= startTime) {
    // pingPong and oscillate return to start/rest before the window
    if (mode === "pingPong") return startValue;
    if (mode === "oscillate") return (startValue + endValue) / 2;
    return startValue;
  }
  // After animation window
  if (time >= endTime) {
    // pingPong returns to start, oscillate returns to center (rest)
    if (mode === "pingPong") return startValue;
    if (mode === "oscillate") return (startValue + endValue) / 2;
    return endValue;
  }

  // Within window - interpolate
  const duration = endTime - startTime;
  if (duration <= 0) return endValue;

  const t = (time - startTime) / duration;
  const speed = speedMultiplier ?? 1;

  // PingPong mode: triangle wave — goes start→end in first half, end→start in second half
  // Speed multiplies the cycle rate (speed 2x = 2 full cycles within the window)
  if (mode === "pingPong") {
    const scaledT = (t * speed) % 1;
    const pingPongT = scaledT < 0.5 ? scaledT * 2 : (1 - scaledT) * 2;
    const easingFn = curveToEasingFn(curve);
    const easedT = Math.max(0, Math.min(1, easingFn(pingPongT)));
    return startValue + (endValue - startValue) * easedT;
  }

  // Oscillate mode: dampened sine wave with N cycles
  // Speed multiplies the oscillation frequency (speed 1.5x with 4 oscillations = 6 effective oscillations)
  if (mode === "oscillate") {
    const count = oscillations ?? 3;
    const center = (startValue + endValue) / 2;
    const amplitude = (endValue - startValue) / 2;
    // Dampened envelope: peaks in center, fades at edges (smooth start/end)
    // Envelope uses raw t to always span the full window
    const envelope = Math.sin(Math.PI * t); // 0 at t=0, 1 at t=0.5, 0 at t=1
    const oscillation = Math.sin(2 * Math.PI * count * speed * t);
    return center + amplitude * oscillation * envelope;
  }

  // Default "once" mode: standard one-way interpolation
  // Speed is NOT applied here — the timing window boundaries define the animation speed.
  const easingFn = curveToEasingFn(curve);
  const easedT = Math.max(0, Math.min(1, easingFn(t)));

  return startValue + (endValue - startValue) * easedT;
}

// ============ SINGLE EFFECT EVALUATION ============

/**
 * Evaluate a single MotionConfig at a given time
 * Returns contribution values (offsets/factors) for composition
 */
function evaluateSingleEffect(
  config: MotionConfig,
  time: number,
  element: Element,
  canvasWidth: number,
  canvasHeight: number
): EffectContribution {
  const contribution: EffectContribution = {
    dx: 0,
    dy: 0,
    scaleFactorX: 1,
    scaleFactorY: 1,
    rotationOffset: 0,
    opacityFactor: 1,
  };

  // Speed multiplier is passed into interpolateValue so it affects the
  // internal animation rate (oscillation/pingPong speed) WITHOUT shifting
  // the timing window.  This ensures Global Timing is always respected.
  const speedMultiplier =
    config.speed?.enabled && config.speed.multiplier
      ? config.speed.multiplier
      : 1;

  // Direction (position offset)
  if (config.direction?.enabled) {
    const dir = config.direction;
    const toPixelsX = (val: number) =>
      dir.usePercent ? (val / 100) * canvasWidth : val;
    const toPixelsY = (val: number) =>
      dir.usePercent ? (val / 100) * canvasHeight : val;

    // Interpolate offsets
    const startOffsetX = toPixelsX(dir.startX);
    const endOffsetX = toPixelsX(dir.endX);
    const startOffsetY = toPixelsY(dir.startY);
    const endOffsetY = toPixelsY(dir.endY);

    contribution.dx = interpolateValue(
      time,
      dir.startTime,
      dir.endTime,
      startOffsetX,
      endOffsetX,
      dir.curve,
      dir.mode,
      dir.oscillations,
      speedMultiplier
    );
    contribution.dy = interpolateValue(
      time,
      dir.startTime,
      dir.endTime,
      startOffsetY,
      endOffsetY,
      dir.curve,
      dir.mode,
      dir.oscillations,
      speedMultiplier
    );
  }

  // Scale (scale factor)
  if (config.scale?.enabled) {
    const scale = config.scale;

    if (scale.lockRatio) {
      // Uniform scaling
      const factor = interpolateValue(
        time,
        scale.startTime,
        scale.endTime,
        scale.startScale,
        scale.endScale,
        scale.curve,
        scale.mode,
        scale.oscillations,
        speedMultiplier
      );
      contribution.scaleFactorX = factor;
      contribution.scaleFactorY = factor;
    } else {
      // Non-uniform scaling
      const startSX = scale.startScaleX ?? scale.startScale;
      const endSX = scale.endScaleX ?? scale.endScale;
      const startSY = scale.startScaleY ?? scale.startScale;
      const endSY = scale.endScaleY ?? scale.endScale;

      contribution.scaleFactorX = interpolateValue(
        time,
        scale.startTime,
        scale.endTime,
        startSX,
        endSX,
        scale.curve,
        scale.mode,
        scale.oscillations,
        speedMultiplier
      );
      contribution.scaleFactorY = interpolateValue(
        time,
        scale.startTime,
        scale.endTime,
        startSY,
        endSY,
        scale.curve,
        scale.mode,
        scale.oscillations,
        speedMultiplier
      );
    }
  }

  // Zoom (also a scale factor)
  if (config.zoom?.enabled) {
    const zoom = config.zoom;
    const zoomFactor = interpolateValue(
      time,
      zoom.startTime,
      zoom.endTime,
      zoom.startZoom,
      zoom.endZoom,
      zoom.curve,
      zoom.mode,
      zoom.oscillations,
      speedMultiplier
    );
    // Zoom multiplies with scale
    contribution.scaleFactorX *= zoomFactor;
    contribution.scaleFactorY *= zoomFactor;
  }

  // Rotation (rotation offset)
  if (config.rotation?.enabled) {
    const rot = config.rotation;
    let startDeg = rot.startDegrees;
    let endDeg = rot.endDegrees;

    // Handle CCW direction
    if (rot.direction === "ccw" && endDeg > startDeg) {
      endDeg = startDeg - (endDeg - startDeg);
    }

    contribution.rotationOffset = interpolateValue(
      time,
      rot.startTime,
      rot.endTime,
      startDeg,
      endDeg,
      rot.curve,
      rot.mode,
      rot.oscillations,
      speedMultiplier
    );
  }

  // Opacity (opacity factor)
  if (config.opacity?.enabled) {
    const op = config.opacity;
    // Convert opacity values to factor relative to 1
    // If opacity goes 0 → 1, factor goes 0 → 1
    // If opacity goes 1 → 0, factor goes 1 → 0
    contribution.opacityFactor = interpolateValue(
      time,
      op.startTime,
      op.endTime,
      op.startValue,
      op.endValue,
      op.curve,
      op.mode,
      op.oscillations,
      speedMultiplier
    );
  }

  return contribution;
}

// ============ COMPOSITION ============

/**
 * Compose multiple effect contributions into final transform
 * Position: additive (sum of offsets)
 * Scale: multiplicative (product of factors)
 * Rotation: additive (sum of offsets)
 * Opacity: multiplicative (product of factors)
 */
function composeContributions(
  contributions: EffectContribution[],
  baseProps: ComposedTransform,
  element: Element
): ComposedTransform {
  // Start with base values
  let totalDx = 0;
  let totalDy = 0;
  let totalScaleX = 1;
  let totalScaleY = 1;
  let totalRotation = 0;
  let totalOpacity = 1;

  // Accumulate contributions
  for (const c of contributions) {
    // Position is additive
    totalDx += c.dx;
    totalDy += c.dy;

    // Scale is multiplicative (clamped to prevent collapse to zero)
    totalScaleX *= c.scaleFactorX;
    totalScaleY *= c.scaleFactorY;

    // Rotation is additive
    totalRotation += c.rotationOffset;

    // Opacity is multiplicative
    totalOpacity *= c.opacityFactor;
  }

  // Prevent scale collapse: multiplicative stacking can drive scale to near-zero
  totalScaleX = Math.max(0.001, totalScaleX);
  totalScaleY = Math.max(0.001, totalScaleY);

  const baseScaleX = baseProps.scaleX;
  const baseScaleY = baseProps.scaleY;

  // Final scale = baseScale * totalScaleFactor
  const finalScaleX = baseScaleX * totalScaleX;
  const finalScaleY = baseScaleY * totalScaleY;

  // Final position = base + direction offsets
  // NOTE: No scale compensation needed here — the Konva renderer uses
  // center-origin transforms (offsetX/offsetY = halfW/halfH) so scaling
  // and rotation naturally pivot around the element's center.
  const finalX = baseProps.x + totalDx;
  const finalY = baseProps.y + totalDy;

  // Final rotation = base + rotation offsets
  const finalRotation = baseProps.rotation + totalRotation;

  // Final opacity = base * opacity factors (clamped to 0.01-1)
  // Floor of 0.01 prevents motion effects from making elements fully invisible;
  // element visibility is controlled by the visible/timelineVisible flags instead.
  const finalOpacity = Math.max(0.01, Math.min(1, baseProps.opacity * totalOpacity));

  return {
    x: Number.isFinite(finalX) ? finalX : baseProps.x,
    y: Number.isFinite(finalY) ? finalY : baseProps.y,
    scaleX: Number.isFinite(finalScaleX) ? finalScaleX : baseProps.scaleX,
    scaleY: Number.isFinite(finalScaleY) ? finalScaleY : baseProps.scaleY,
    rotation: Number.isFinite(finalRotation) ? finalRotation : baseProps.rotation,
    opacity: Number.isFinite(finalOpacity) ? finalOpacity : baseProps.opacity,
  };
}

// ============ MAIN API ============

/**
 * Evaluate all motion effects and compose into final transform
 *
 * @param effects - Array of motion effects to evaluate
 * @param time - Current playhead time (LOCAL time, relative to element start)
 * @param element - The element being animated
 * @param baseProps - Base transform values (element's static properties)
 * @param options - Canvas dimensions for percentage calculations
 */
export function evaluateMotionEffects(
  effects: MotionEffect[],
  time: number,
  element: Element,
  baseProps: ComposedTransform,
  options: { canvasWidth?: number; canvasHeight?: number; globalTime?: number } = {}
): ComposedTransform {
  if (!effects || effects.length === 0) {
    return baseProps;
  }

  const { canvasWidth = 1920, canvasHeight = 1080, globalTime } = options;

  // Sort effects by priority (lower first)
  const sortedEffects = [...effects]
    .filter((effect) => effect.enabled !== false)
    .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));

  // Evaluate each effect
  const contributions: EffectContribution[] = [];
  for (const effect of sortedEffects) {
    const timingBase = effect.config?.timeBase ?? effect.timeBase ?? "local";
    const effectTime = timingBase === "global" && typeof globalTime === "number" ? globalTime : time;
    const contribution = evaluateSingleEffect(
      effect.config,
      effectTime,
      element,
      canvasWidth,
      canvasHeight
    );
    contributions.push(contribution);
  }

  // Compose all contributions
  return composeContributions(contributions, baseProps, element);
}

/**
 * Create a new MotionEffect from a preset and config
 */
export function createMotionEffect(
  presetId: string,
  presetName: string,
  config: MotionConfig,
  priority?: number
): MotionEffect {
  return {
    id: `${presetId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    presetId,
    name: presetName,
    config,
    enabled: true,
    priority,
    timeBase: config.timeBase ?? "local",
  };
}

/**
 * Add an effect to the effects array, optionally replacing same-preset effects
 */
export function addMotionEffect(
  effects: MotionEffect[],
  newEffect: MotionEffect,
  replacesSamePreset: boolean = true
): MotionEffect[] {
  if (replacesSamePreset) {
    // Remove existing effects from the same preset
    const filtered = effects.filter((e) => e.presetId !== newEffect.presetId);
    return [...filtered, newEffect];
  }
  return [...effects, newEffect];
}

/**
 * Remove an effect by ID
 */
export function removeMotionEffect(
  effects: MotionEffect[],
  effectId: string
): MotionEffect[] {
  return effects.filter((e) => e.id !== effectId);
}

/**
 * Remove all effects with a given preset ID
 */
export function removeMotionEffectsByPreset(
  effects: MotionEffect[],
  presetId: string
): MotionEffect[] {
  return effects.filter((e) => e.presetId !== presetId);
}

/**
 * Update an effect's config
 */
export function updateMotionEffect(
  effects: MotionEffect[],
  effectId: string,
  config: MotionConfig
): MotionEffect[] {
  return effects.map((e) =>
    e.id === effectId ? { ...e, config } : e
  );
}

/**
 * Enable/disable an effect by ID (preserves config)
 */
export function setMotionEffectEnabled(
  effects: MotionEffect[],
  effectId: string,
  enabled: boolean
): MotionEffect[] {
  return effects.map((e) =>
    e.id === effectId ? { ...e, enabled } : e
  );
}

/**
 * Check if any effect is active at a given time
 */
export function hasActiveEffects(effects: MotionEffect[], time: number): boolean {
  if (!effects || effects.length === 0) return false;

  for (const effect of effects) {
    const config = effect.config;

    // Check each property for activity
    if (config.direction?.enabled) {
      const dir = config.direction;
      if (time >= dir.startTime && time <= dir.endTime) return true;
    }
    if (config.scale?.enabled) {
      const scale = config.scale;
      if (time >= scale.startTime && time <= scale.endTime) return true;
    }
    if (config.zoom?.enabled) {
      const zoom = config.zoom;
      if (time >= zoom.startTime && time <= zoom.endTime) return true;
    }
    if (config.rotation?.enabled) {
      const rot = config.rotation;
      if (time >= rot.startTime && time <= rot.endTime) return true;
    }
    if (config.opacity?.enabled) {
      const op = config.opacity;
      if (time >= op.startTime && time <= op.endTime) return true;
    }
  }

  return false;
}

/**
 * Get summary of active effects for UI display
 */
export function getActiveEffectsSummary(effects: MotionEffect[]): string[] {
  return effects.map((e) => e.name);
}
